import { useMemo } from 'react';

import { drillDownTree } from '@/app/routes';
import type { NavItem } from '@/components/Sidebar/sidebar.types';
import { useCurrentUser } from '@/features/users/users.hooks';
import { matchDrillDownPath } from './matcher';
import { STACK_SEPARATOR } from './stack-path';
import type { DrillDownNode, DrillDownPath } from './types';

/**
 * Route access for external (customer) accounts.
 *
 * Internal users may open everything. External users
 * (`SessionUser.internal === false`) are limited to a whitelist expressed as
 * metadata on the drill-down nodes (`access`, see routes.ts); a node without
 * it is internal-only. A URL is allowed when every node on its matched path
 * is allowed, so a whitelisted leaf under an internal-only parent stays
 * closed.
 *
 * Every surface that decides whether to render a screen or a link to it —
 * the stack renderer, the sidebar menus, the pins — goes through the helpers
 * below, so they always agree.
 */

export type NodeAccess = 'internal' | 'all';

/**
 * Static access, or a rule over the node's matched params when only some
 * values of an optional segment are open (the project details tabs, the
 * settings tabs).
 */
export type AccessRule =
  | NodeAccess
  | ((params: Record<string, string>) => NodeAccess);

export interface AccessMeta {
  /** Who may open this node. Defaults to `'internal'`. */
  access?: AccessRule;
}

/** The part of the session user the access checks need. */
export type AccessUser = { internal: boolean } | null | undefined;

/** Without a session nobody is internal, so the checks fail closed. */
export function isInternalUser(user: AccessUser): boolean {
  return user?.internal === true;
}

export function nodeAccess<T extends AccessMeta>(
  node: DrillDownNode<T>,
  params: Record<string, string> = {},
): NodeAccess {
  const rule = node.access ?? 'internal';
  return typeof rule === 'function' ? rule(params) : rule;
}

export function isNodeAllowed<T extends AccessMeta>(
  node: DrillDownNode<T>,
  user: AccessUser,
  params: Record<string, string> = {},
): boolean {
  return isInternalUser(user) || nodeAccess(node, params) === 'all';
}

/** Every node on the path must be allowed; an empty (unmatched) path is not. */
export function isPathAllowed<T extends AccessMeta>(
  path: DrillDownPath<T>,
  user: AccessUser,
): boolean {
  if (isInternalUser(user)) return true;
  return (
    path.length > 0 &&
    path.every(({ node, params }) => isNodeAllowed(node, user, params))
  );
}

/**
 * Resolve `url` against `tree` and check every stack in it (`/a/~/b`). The
 * query string and hash are ignored; a URL that matches no node is closed
 * for externals.
 */
export function isUrlAllowedIn<T extends AccessMeta>(
  tree: DrillDownNode<T>[],
  url: string,
  user: AccessUser,
): boolean {
  if (isInternalUser(user)) return true;
  const [pathname = ''] = url.split(/[?#]/);
  return pathname.split(STACK_SEPARATOR).every((stack) => {
    const segments = stack.split('/').filter(Boolean);
    // An empty stack ('/' or the empty secondary stack) opens nothing.
    if (segments.length === 0) return true;
    return isPathAllowed(matchDrillDownPath(tree, segments), user);
  });
}

/**
 * Sidebar menu for `user`. Internal users get the menu untouched (including
 * the placeholder entries without a URL); externals keep only the items that
 * lead to a screen they may open, and groups that still have one.
 */
export function filterNavItems<T extends AccessMeta>(
  tree: DrillDownNode<T>[],
  items: NavItem[],
  user: AccessUser,
): NavItem[] {
  if (isInternalUser(user)) return items;
  const allowed = (url?: string) => !!url && isUrlAllowedIn(tree, url, user);
  return items.flatMap((item) => {
    if (item.url) return allowed(item.url) ? [item] : [];
    const subItems = (item.items ?? []).filter((sub) => allowed(sub.url));
    return subItems.length > 0 ? [{ ...item, items: subItems }] : [];
  });
}

// ---------------------------------------------------------------------------
// Bound to the application's route tree
// ---------------------------------------------------------------------------

/** May `user` open `url` in this app? */
export function isUrlAllowed(url: string, user: AccessUser): boolean {
  return isUrlAllowedIn(drillDownTree, url, user);
}

/**
 * Access checks bound to the current session, for components. Re-renders
 * when the session changes (sign in / out).
 */
export function useIsAllowed() {
  const user = useCurrentUser();
  return useMemo(
    () => ({
      /** Internal account: everything is allowed. */
      internal: isInternalUser(user),
      isUrlAllowed: (url?: string) => !!url && isUrlAllowed(url, user),
      isPathAllowed: (path: DrillDownPath<AccessMeta>) =>
        isPathAllowed(path, user),
      filterNavItems: (items: NavItem[]) =>
        filterNavItems(drillDownTree, items, user),
    }),
    [user],
  );
}
